/**
 * Classify gae* diary for Jaeger: tree spans vs root-only events.
 *
 * app.layer values:
 *   journey — business spans (request.load, send.process, …)
 *   diary   — process breadcrumbs (short child spans under ambient journey)
 *   ui      — chrome (popup SHOW/CLOSE, menus, cron) — session root events only
 */

export const OTEL_ATTR_APP_LAYER = 'app.layer';

export const APP_LAYER = {
  JOURNEY: 'journey',
  DIARY: 'diary',
  UI: 'ui'
};

/** Entire categories are UI chrome (no tree rows). */
const UI_CATEGORIES = new Set([
  'INFO_POP_UP',
  'CONFIRM_BIODATA_POP_UP',
  'REJECT_DOCUMENT_POPUP',
  'SELECT_WAY_TO_SIGN_POP_UP',
  'IDCHEK_ON_MOBILE_POPUP',
  'EXTERNAL_POPUP',
  'CRON_JOB'
]);

/** Actions that are UI chrome regardless of category. */
const UI_ACTIONS = new Set([
  'SHOW',
  'CLOSE',
  'UPLOAD_ACTIONS_MENU',
  'DOCUMENT_PAGES_POPUP',
  'RESTORE_SAVED_DOCUMENTS_DIALOG',
  'DIALOG_DELETE_ALL_PAGES',
  'DIALOG_DELETE_SELECTED_PAGES',
  'SHOW_ALERT',
  'SHOW_RELOAD_IMAGE_MENU',
  'SHOW_CONFIRM_DATA_LOOSE'
]);

/**
 * @param {unknown} category
 * @param {unknown} action
 * @returns {'ui'|'diary'}
 */
export function classifyAnalyticsLayer(category, action) {
  const cat =
    category === undefined || category === null ? '' : String(category);
  const act = action === undefined || action === null ? '' : String(action);

  if (UI_CATEGORIES.has(cat)) {
    return APP_LAYER.UI;
  }
  if (UI_ACTIONS.has(act)) {
    return APP_LAYER.UI;
  }
  for (const uiAction of UI_ACTIONS) {
    if (act.startsWith(`${uiAction}.`)) {
      return APP_LAYER.UI;
    }
  }
  if (act.startsWith('DIALOG_')) {
    return APP_LAYER.UI;
  }
  // Popup / menu categories that still carry process actions (OTP, cron, …):
  // only SHOW/CLOSE (and similar) are UI — handled above.
  return APP_LAYER.DIARY;
}

const ANALYTICS_ERROR_EXCLUDE = /FALSE_POSITIVE/i;

/** `gaeError`, `ERROR_ON_*`, `*_ERROR`, `.ERROR.*`, etc. */
const ANALYTICS_ERROR_ACTION_PATTERNS = [
  /^ERROR$/i,
  /\.ERROR$/i,
  /ERROR_ON/i,
  /ERR_ON/i,
  /CRASH_ON/i,
  /_ERROR$/i,
  /\.ERROR[._]/i,
  /^ERROR_/i,
  /_ERRORS$/i
];

function analyticsTextParts(category, action, label) {
  const act = action === undefined || action === null ? '' : String(action);
  const lab =
    label === undefined || label === null ? '' : String(label).slice(0, 500);
  return { act, lab, combined: `${act} ${lab}` };
}

/**
 * Whether a gae* diary event should be exported as an OTel ERROR span.
 * @param {unknown} category
 * @param {unknown} action
 * @param {unknown} label
 */
export function isAnalyticsErrorEvent(category, action, label) {
  const { act, lab, combined } = analyticsTextParts(category, action, label);
  if (!act && !lab) return false;
  if (ANALYTICS_ERROR_EXCLUDE.test(combined)) return false;
  if (ANALYTICS_ERROR_ACTION_PATTERNS.some(rx => rx.test(act))) return true;
  if (/ERROR_ON|ERR_ON|CRASH_ON/i.test(lab)) return true;
  return false;
}

/**
 * @param {unknown} category
 * @param {unknown} action
 * @param {unknown} label
 */
export function getAnalyticsErrorMessage(category, action, label) {
  const { act, lab } = analyticsTextParts(category, action, label);
  if (act === 'ERROR' && lab) return lab;
  if (lab) return lab;
  return act || String(category ?? 'analytics_error');
}
